import { Tier } from '@backrs_ui/common-ui';

import { SchoolLevel } from '@backrs_ui/grow-ui/src/models/models';

export type MarkersOfSuccessItem = Readonly<{
  title?: string;
  description: string;
}>;

// we exclude 'SemiSkilled' and 'Entrepreneurship' tiers as they don't have markers of success
export type MarkersOfSuccess = Record<
  Exclude<SchoolLevel, 'no_current_enrollment' | 'trade'>,
  Record<Exclude<Tier, 'Semi-skilled' | 'Entrepreneurship'>, Record<string, MarkersOfSuccessItem[]>>
>;

export const markersOfSuccess: MarkersOfSuccess = {
  high_school: {
    'Middle-Tier Professional': {
      'Post-secondary objectives': [
        { description: 'Plan to pursue a at least a somewhat rigorous major' },
        { description: 'Ensure access to sufficient {{financial aid}}' },
      ],
      Academics: [
        { description: 'Minimum GPA 3.0 on 4.0 scale' },
        { description: 'At least some honors, accelerated, and/or AP classes' },
        { description: 'SAT: 1100+ or ACT: 22+' },
        {
          description:
            'Demonstrate engagement including school clubs, community service, and job-related activities',
        },
      ],
      'Career Readiness': [
        {
          description:
            'Review the profiles of at least 10 people that have succeeded as middle-tier professionals to understand their choices and learn from their successes and set-backs - speak to as many as you can',
        },
        { description: 'Exposure to at least 20 potential career paths' },
        {
          description: 'Shadow (or tag along) with at least 5 people in different types of jobs',
        },
        { description: 'Secure part-time jobs or internships to demonstrate {{work ethic}}' },
        {
          description:
            'Build connections with at least 5 middle-tier professionals to help you on your journey',
        },
      ],
      Fundamentals: [
        {
          description:
            'Develop basic proficiency with {{goal setting}}, {{learning}}, {{seeking support}}, {{problem solving}}, {{decision making}}, {{verbal communication}}, {{written communication}}, good {{habits}}, {{collaboration}}, and {{time management}}',
        },
      ],
      Wellness: [
        {
          description:
            'Build basic proficiency with navigating {{feelings}}, dealing with {{stress}}, {{nutrition}}, {{fitness}}, and {{sleep}}',
        },
        { description: 'Friends that are mostly good influences' },
        { description: 'Avoiding unhealthy relationships and unplanned pregnancies' },
        {
          description:
            'Avoiding adverse events including illicit drug use, disciplinary issues, and crime',
        },
      ],
      Finances: [
        {
          description:
            'Access to at least $1000 of savings to cover out of pocket costs associated with additional education',
        },
        {
          description:
            'Most students will need to build deep proficiency with scholarships and {{financial aid}}',
        },
        {
          description: 'Access to funds to cover {{extracurriculars}} and enrichment activities',
        },
        { description: 'Basic fluency with {{budgeting}}, {{credit / debt}}, {{banking}}' },
      ],
      Mindsets: [
        { description: 'Willingness to work hard and make some sacrifices for the future' },
        {
          description:
            'Basic proficiency with concepts of {{agency}}, {{growth mindset}}s, {{courage}}, {{work ethic}}, {{generosity}}, {{responsibility}}, {{gratitude}}, {{resilience}}, {{empathy}}, {{respect}}, and {{curiosity}}',
        },
      ],
    },
    Trades: {
      'Post-secondary objectives': [
        {
          description:
            'Gain admission and financial support to a high quality trade schools, higher performing community colleges with relevant programs and/or apprenticeship programs',
        },
      ],
      Academics: [
        { description: 'Maintain GPA of 2.5+' },
        { description: 'Focus on math, science, and vocational courses' },
        { description: 'Pass basic competency tests; Certification exams for trades' },
        {
          description: 'Engage in clubs, skills competitions and community service projects',
        },
      ],
      'Career Readiness': [
        {
          description:
            'Review the profiles of at least 10 people that have succeeded on a trade or technical path to understand their choices and learn from their successes and set-backs - speak to as many as you can',
        },
        {
          description: 'Exposure to at least 20 different potential trades or technical paths',
        },
        {
          description:
            'Shadow (or tag along) with at least 5 people in different types of jobs to get a sense of their work',
        },
        { description: 'Secure part-time jobs or internships to demonstrate {{work ethic}}' },
        {
          description:
            'Build connections with mentors in trades and/or via professional organizations',
        },
      ],
      Fundamentals: [
        {
          description:
            'Develop basic proficiency with {{goal setting}}, {{learning}}, {{seeking support}}, {{problem solving}}, {{decision making}}, {{verbal communication}}, {{written communication}}, good {{habits}}, {{collaboration}}, and {{time management}}',
        },
        {
          description:
            'Once you determine your target path, start to build the needed technical skills through hands-on practice and potentially certifications',
        },
      ],
      Wellness: [
        {
          description:
            'Build basic proficiency with navigating {{feelings}}, dealing with {{stress}}, {{nutrition}}, {{fitness}}, and {{sleep}}',
        },
        { description: 'Friends that are mostly good influences' },
        { description: 'Avoiding unhealthy relationships and unplanned pregnancies' },
        {
          description:
            'Avoiding adverse events including illicit drug use, disciplinary issues, and crime',
        },
      ],
      Finances: [
        {
          description:
            'Access to at least $1000 of savings to cover out of pocket costs associated with additional education',
        },
        {
          description:
            'Most students will need to build deep proficiency with scholarships and {{financial aid}}',
        },
        { description: 'Access to funds to cover {{extracurriculars}}' },
        { description: 'Basic fluency with {{budgeting}}, {{credit / debt}}, {{banking}}' },
      ],
      Mindsets: [
        { description: 'Commitment to reliability, punctuality' },
        {
          description:
            'Basic proficiency with concepts of {{agency}}, {{growth mindset}}, {{courage}}, {{work ethic}}, {{generosity}}, {{responsibility}}, {{gratitude}}, {{resilience}}, {{empathy}}, {{respect}}, and {{curiosity}}',
        },
      ],
    },
    'Upper-Tier Professional': {
      'Post-secondary objectives': [
        {
          description:
            'Earn admission and matriculate to at least a selective university, and ideally a very selective university or top-tier university',
        },
        { description: 'Plan to pursue a rigorous, competitive major' },
        { description: 'Ensure access to sufficient {{financial aid}}' },
      ],
      Academics: [
        { description: 'Minimum GPA 3.5 on 4.0 scale' },
        { description: 'Mostly honors, accelerated, AP courses' },
        { description: 'Meaningful academic recognition' },
        { description: 'SAT: 1300+ or ACT: 28+; AP scores of 3 and 4s' },
        {
          description:
            'Very strong extracurricular profile including examples of leadership and achievement',
        },
      ],
      'Career Readiness': [
        {
          description:
            'Review the profiles of at least 10 successful upper-tier professionals to understand their choices and learn from their successes and set-backs. Speak to as many as you can.',
        },
        { description: 'Exposure to at least 20 potential career paths' },
        {
          description: 'Shadow (or tag along) with at least 5 people in different types of jobs',
        },
        { description: 'Secure part-time jobs or internships to demonstrate {{work ethic}}' },
        {
          description:
            'Build connections with at least 5 upper-tier professionals to help you on your journey',
        },
      ],
      Skills: [
        {
          description:
            'Develop basic proficiency with {{goal setting}}, {{learning}}, {{seeking support}}, {{problem solving}}, {{decision making}}, {{verbal communication}}, {{written communication}}, good {{habits}}, {{collaboration}}, and {{time management}}',
        },
        {
          description: 'Build advanced proficiency in at least a few of these before college',
        },
      ],
      Health: [
        {
          description:
            'Build basic proficiency with navigating {{feelings}}, dealing with {{stress}}, {{nutrition}}, {{fitness}}, and {{sleep}}',
        },
        { description: 'Friends that are mostly good influences' },
        { description: 'Avoiding unhealthy relationships and unplanned pregnancies' },
        {
          description:
            'Avoiding adverse events including illicit drug use, disciplinary issues, and crime',
        },
      ],
      Finances: [
        {
          description:
            'Access to at least $1000 of savings to cover out of pocket costs associated with additional education',
        },
        {
          description:
            'Most students will need to build deep proficiency with scholarships and {{financial aid}}',
        },
        {
          description: 'Access to funds to cover {{extracurriculars}} and enrichment activities',
        },
        { description: 'Basic fluency with {{budgeting}}, {{credit / debt}}, {{banking}}' },
      ],
      Mindsets: [
        { description: 'Willingness to work very hard and make sacrifices for the future' },
        {
          description:
            'Basic proficiency with concepts of {{agency}}, {{growth mindset}}s, {{courage}}, {{work ethic}}, {{generosity}}, {{responsibility}}, {{gratitude}}, {{resilience}}, {{empathy}}, {{respect}}, and {{curiosity}}',
        },
      ],
    },
    'Top-Tier Professional': {
      'Post-secondary objectives': [
        {
          description:
            'Earn admission and matriculate to at least a very selective university, and ideally a top-tier university',
        },
        { description: 'Plan to pursue a rigorous, competitive major' },
        { description: 'Ensure access to sufficient {{financial aid}}' },
      ],
      Academics: [
        { description: 'Minimum GPA 3.8 on 4.0 scale' },
        { description: 'All honors, accelerated, and/or AP courses' },
        { description: 'Distinctive academic recognition' },
        { description: 'SAT: 1400+ or ACT: 33+; AP scores of 4 and 5s' },
        {
          description:
            'Exceptional extracurricular profile including examples of distinctive leadership and achievement',
        },
      ],
      'Career Readiness': [
        {
          description:
            'Review the profiles of at least 10 successful top-tier professionals to understand their choices and learn from their successes and set-backs. Speak to as many as you can.',
        },
        { description: 'Exposure to at least 20 potential career paths' },
        {
          description:
            'Shadow (or tag along) with at least 5 people in different types of jobs where a top-tier track exists',
        },
        { description: 'Secure part-time jobs or internships to demonstrate {{work ethic}}' },
        {
          description:
            'Build connections with at least 5 top-tier professionals to help you on your journey',
        },
      ],
      Fundamentals: [
        {
          description:
            'Develop basic proficiency with {{goal setting}}, {{learning}}, {{seeking support}}, {{problem solving}}, {{decision making}}, {{verbal communication}}, {{written communication}}, {{good habits}}, {{collaboration}}, and {{time management}}',
        },
        {
          description: 'Build advanced proficiency in as many of these as you can before college',
        },
      ],
      Wellness: [
        {
          description:
            'Build basic proficiency with navigating {{feelings}}, dealing with {{stress}}, {{nutrition}}, {{fitness}}, and {{sleep}}',
        },
        { description: 'Friends that are mostly good influences' },
        { description: 'Avoiding unhealthy relationships and unplanned pregnancies' },
        {
          description:
            'Avoiding adverse events including illicit drug use, disciplinary issues, and crime',
        },
      ],
      Finances: [
        {
          description:
            'Access to at least $1000 of savings to cover out of pocket costs associated with additional education',
        },
        {
          description:
            'Most students will need to build deep proficiency with scholarships and {{financial aid}}',
        },
        {
          description: 'Access to funds to cover {{extracurriculars}} and enrichment activities',
        },
        { description: 'Basic fluency with {{budgeting}}, {{credit / debt}}, {{banking}}' },
      ],
      Mindsets: [
        {
          description:
            'Willingness to work extremely hard and make substantial sacrifices for the future',
        },
        {
          description:
            'Basic proficiency with concepts of {{agency}}, {{growth mindset}}s, {{courage}}, {{work ethic}}, {{generosity}}, {{responsibility}}, {{gratitude}}, {{resilience}}, {{empathy}}, {{respect}}, and {{curiosity}}',
        },
      ],
    },
  },
  college: {
    'Middle-Tier Professional': {
      'Post-secondary objectives': [
        {
          title: 'Pursue a Relevant Major:',
          description:
            'Stay enrolled in a reputable degree program that aligns with middle-tier professional careers (e.g., business administration, healthcare, education, information technology). Avoid less rigorous majors with little direct relevance to potential jobs.',
        },
        {
          description:
            'Consider elective courses that enhance employability, such as analytics, project management, and communications.',
        },
        {
          title: 'Engage in Career Development Programs:',
          description:
            'Participate in career services programs offered by the university, including job fairs, {{resume}} workshops, and career counseling, to prepare for job searches and {{interview}}s.',
        },
        {
          title: 'Build a Supportive Network:',
          description:
            'Develop connections with faculty, career advisors, and alumni to gain mentorship, career advice, and job placement assistance.',
        },
      ],
      Academics: [
        {
          title: 'Maintain a Solid GPA:',
          description:
            'Aim for a minimum GPA of 3.0+ on a 4.0 scale to demonstrate consistent academic performance. Focus on doing well in courses directly related to your chosen career path.',
        },
        {
          title: 'Complete Practical Coursework:',
          description:
            'Enroll in courses that provide practical skills, such as technical skills, data analysis, or project management. Participate in hands-on learning opportunities like labs, practicums, or applied projects.',
        },
        {
          title: 'Academic Engagement:',
          description:
            'Take part in group projects, presentations, or case studies that mirror real-world scenarios. Seek opportunities for academic recognition, like class awards or positive faculty feedback.',
        },
      ],
      Careers: [
        {
          title: 'Secure Internships and Work Experience:',
          description:
            'Obtain {{internships}}, co-op positions, or part-time work in your field of interest to gain relevant experience, develop skills, and build your resume.',
        },
        {
          title: 'Attend Career Workshops:',
          description:
            "Participate in workshops on resume writing, {{interview}} preparation, and job search strategies provided by the university's career center.",
        },
        {
          title: 'Develop a Professional Network:',
          description:
            'Engage with professionals in your field through networking events, student organizations, or LinkedIn to build relationships that may assist in job placement.',
        },
        {
          title: 'Create a Professional Portfolio:',
          description:
            'Compile a portfolio of your work, including projects, {{internship}}s, and relevant coursework, to showcase your skills and experience to potential employers.',
        },
      ],
      Fundamentals: [
        {
          title: 'Build Core Competencies:',
          description:
            'Further develop proficiency in essential skills like {{goal setting}}, {{time management}}, teamwork, and problem-solving through coursework, extracurricular activities, and {{internships}}.',
        },
        {
          title: 'Seek Constructive Feedback:',
          description:
            'Actively seek feedback from professors, supervisors, and peers to identify areas for improvement and strengthen your skillset.',
        },
        {
          title: 'Adaptability and Initiative:',
          description:
            'Show adaptability and take initiative in academic and work settings by seeking out new learning opportunities and taking on responsibilities.',
        },
      ],
      Wellness: [
        {
          title: 'Maintain a Healthy Routine:',
          description:
            'Prioritize your well-being by maintaining a balanced routine that includes regular exercise, healthy eating, {{stress}} management practices, and adequate sleep.',
        },
        {
          title: 'Utilize Support Systems:',
          description:
            'Access campus wellness resources, such as counseling services, health centers, and peer support groups, to navigate challenges and maintain mental and emotional health.',
        },
        {
          title: 'Build Positive Relationships:',
          description:
            'Cultivate a supportive social circle that encourages personal and academic growth. Focus on building relationships that promote a healthy work-life balance.',
        },
      ],
      Finances: [
        {
          title: 'Develop Financial Literacy:',
          description:
            'Continue building financial literacy by managing a personal budget, understanding student loans, and learning about {{savings}} and {{credit}} management.',
        },
        {
          title: 'Apply for financial aid and Scholarships:',
          description:
            'Actively seek out scholarships, grants, and {{financial aid}} opportunities to support your education and minimize debt.',
        },
        {
          title: 'Gain Part-Time Employment:',
          description:
            'Consider part-time work or work-study programs to supplement income, gain experience, and develop {{budgeting}} skills.',
        },
        {
          title: 'Build savings of at least $2500:',
          description:
            'Save to support {{interviewing}} and to help transition to fully independent living post-graduation.',
        },
      ],
      Mindsets: [
        {
          title: 'Commitment to Growth:',
          description:
            'Embrace a {{growth mindset}} by seeking opportunities for learning and improvement. Be open to constructive feedback and willing to adapt to new challenges.',
        },
        {
          title: 'Work Ethic and Responsibility:',
          description:
            'Demonstrate a strong {{work ethic}} by meeting deadlines, fulfilling commitments, and taking responsibility for your academic and professional growth.',
        },
        {
          title: 'Practical Optimism:',
          description:
            'Maintain a positive outlook while being realistic about career goals and challenges. Approach setbacks as learning experiences and stay motivated toward achieving your career objectives.',
        },
      ],
    },
    Trades: {
      'Post-secondary objectives': [
        {
          title: 'Enroll in a Reputable Trade School or Program:',
          description:
            'Choose a trade school, technical college, or community college program with a strong reputation in your chosen field (e.g., electrical, plumbing, HVAC, welding, automotive technology).',
        },
        {
          title: 'Complete Certification or Licensing Requirements:',
          description:
            'Ensure that your program prepares you for any necessary certifications or licensing exams required for your trade. Plan to complete these requirements as part of your training.',
        },
        {
          title: 'Gain Hands-on Experience:',
          description:
            'Engage in hands-on training, apprenticeships, or work-study programs offered by your school to develop practical skills and build a strong foundation in your trade.',
        },
      ],
      Academics: [
        {
          title: 'Master Core Technical Skills:',
          description:
            'Focus on mastering the core technical skills necessary for your trade through coursework, labs, and hands-on practice. Aim for a high level of proficiency in both basic and advanced techniques.',
        },
        {
          title: 'Maintain a Solid GPA:',
          description:
            'Aim for a minimum GPA of 2.5+ on a 4.0 scale, with a particular emphasis on excelling in trade-specific courses.',
        },
        {
          title: 'Safety and Best Practices:',
          description:
            'Prioritize learning and adhering to safety standards, regulations, and best practices in your trade. Obtain certifications in safety protocols, such as OSHA training.',
        },
        {
          title: 'Continuous Skill Development:',
          description:
            'Participate in additional workshops, certification programs, or specialized training to stay current with industry trends and enhance your technical expertise.',
        },
      ],
      'Career Readiness': [
        {
          title: 'Secure Apprenticeships or Internships:',
          description:
            'Obtain apprenticeships, {{internships}}, or on-the-job training with established professionals in your field to gain real-world experience and apply classroom learning in a practical setting.',
        },
        {
          title: 'Develop a Professional Network:',
          description:
            'Build connections with instructors, classmates, and industry professionals to create a support network for job placement and career advice.',
        },
        {
          title: 'Prepare a Professional Portfolio:',
          description:
            'Compile a portfolio that includes examples of your work, such as projects, completed jobs, or skills demonstrations. Include any certifications, licenses, or awards you have earned.',
        },
        {
          title: 'Attend Job Fairs and Industry Events:',
          description:
            'Participate in job fairs, trade shows, and industry events to meet potential employers, learn about job opportunities, and stay informed about advancements in your field.',
        },
      ],
      Fundamentals: [
        {
          title: 'Build Core Professional Skills:',
          description:
            'Develop key professional skills such as {{time management}}, communication, {{problem solving}}, and teamwork. These skills are crucial for success in both self-employment and working for an employer.',
        },
        {
          title: 'Understand Business Fundamentals:',
          description:
            'Gain a basic understanding of business fundamentals such as customer service, invoicing, and small business management, especially if you plan to start your own business or work as a contractor.',
        },
        {
          title: 'Obtain Basic Tool Proficiency:',
          description:
            'Become proficient in using the tools and equipment relevant to your trade. Familiarize yourself with the maintenance and proper handling of these tools.',
        },
      ],
      Wellness: [
        {
          title: 'Maintain Physical Fitness:',
          description:
            'Keep a level of physical fitness that meets the demands of your trade, as many trades require manual labor and endurance.',
        },
        {
          title: 'Practice Safety and Self-Care:',
          description:
            'Prioritize safety on the job and learn techniques to prevent work-related injuries. Practice self-care to manage {{stress}} and maintain work-life balance.',
        },
        {
          title: 'Build a Support Network:',
          description:
            'Surround yourself with a network of {{friends}}, family, and mentors who support your career goals and offer guidance when needed.',
        },
        {
          description: 'Cultivate friends that are mostly good influences',
        },
        {
          description:
            'Avoiding adverse events including illicit drug use, disciplinary issues, and crime',
        },
      ],
      Finances: [
        {
          title: 'Develop Financial Literacy:',
          description:
            'Build financial literacy by learning to {{budget}}, save, and manage expenses. Understand the costs associated with tools, equipment, and any ongoing certification or licensing fees.',
        },
        {
          title: 'Save for Equipment and Tools:',
          description:
            'Start saving for essential tools and equipment required for your trade. Aim to have a basic set of quality tools by the time you complete your training.',
        },
        {
          title: 'Explore {{financial aid}} and Scholarships:',
          description:
            'Apply for scholarships, grants, or {{financial aid}} specifically available for trade school students to help cover the cost of education and training.',
        },
      ],
      Mindsets: [
        {
          title: 'Commitment to Excellence:',
          description:
            'Demonstrate a strong {{work ethic}}, attention to detail, and a commitment to doing quality work. Strive for excellence in every job, big or small.',
        },
        {
          title: 'Growth and Adaptability:',
          description:
            'Cultivate a mindset of continuous learning and adaptability. Be open to learning new techniques, technologies, and industry practices as your trade evolves.',
        },
        {
          title: 'Pride in Work:',
          description:
            'Take pride in your skills and the work you do. Recognize the value and importance of your trade in contributing to the community and the economy.',
        },
        {
          title: 'Entrepreneurial Spirit:',
          description:
            'If interested in self-employment, develop an entrepreneurial mindset. Be willing to take initiative, seek out clients, and manage the business aspects of your trade effectively.',
        },
      ],
    },
    'Upper-Tier Professional': {
      'Post-secondary objectives': [
        {
          title: 'Pursue a Relevant and Challenging Major:',
          description:
            'Stay enrolled in a competitive major relevant to upper-tier professional careers (e.g., business, engineering, law, healthcare). Consider adding a minor or elective courses that complement your primary field of study.',
        },
        {
          title: 'Engage in Professional Development Programs:',
          description:
            'Participate in university career development programs, workshops, or certifications that align with upper-tier professional roles to strengthen your resume.',
        },
        {
          title: 'Build a Professional Network:',
          description:
            'Actively connect with faculty, alumni, and professionals in your desired field through networking events, career fairs, and student organizations.',
        },
      ],

      Academics: [
        {
          title: 'Maintain a Strong GPA:',
          description:
            'Aim for a minimum GPA of 3.6+ on a 4.0 scale, particularly in major-specific courses, to demonstrate competence and readiness for upper-tier professional roles.',
        },
        {
          title: 'Seek Academic Recognition:',
          description:
            'Strive for academic honors such as Dean’s List, departmental awards, or competitive scholarships. Participate in relevant academic competitions to enhance your profile.',
        },
        {
          title: 'Complete Practical Coursework:',
          description:
            'Enroll in courses with practical applications like project-based learning, case studies, or labs to gain hands-on experience. Consider pursuing a capstone project related to your career interests.',
        },
        {
          title: 'Develop Core Skills:',
          description:
            'Focus on building key skills like data analysis, critical thinking, and effective communication through coursework and extracurricular activities.',
        },
        {
          description:
            'Very strong extracurricular profile including examples of leadership and achievement.',
        },
      ],

      Careers: [
        {
          title: 'Secure Internships and Job Experience:',
          description:
            'Obtain {{internships}} or part-time work relevant to upper-tier professional careers to gain real-world experience and build your resume.',
        },
        {
          title: 'Attend Career Workshops:',
          description:
            'Participate in university-sponsored career readiness workshops to refine your resume, practice {{interview}} skills, and understand job market dynamics.',
        },
        {
          title: 'Join Professional Associations:',
          description:
            'Become an active member of student chapters of professional organizations (e.g., Society of Women Engineers, Pre-Law Society) to access resources and {{networking}} opportunities.',
        },
        {
          title: 'Develop a Professional Online Presence:',
          description:
            'Create a LinkedIn profile highlighting your skills, experience, and achievements. Engage in online professional communities related to your field.',
        },
      ],

      Fundamentals: [
        {
          title: 'Enhance Core Competencies:',
          description:
            'Further develop proficiency in {{goal setting}}, {{time management}}, {{collaboration}}, and effective communication.',
        },
        {
          title: 'Seek Constructive Experiences:',
          description:
            'Engage in group projects, leadership roles, or service-learning opportunities to demonstrate your ability to work well in a team and lead initiatives.',
        },
        {
          title: 'Adaptability and Problem-Solving:',
          description:
            'Hone problem-solving skills and adaptability through practical experiences like {{internships}}, group projects, or volunteer work in challenging environments.',
        },
      ],
      Wellness: [
        {
          title: 'Maintain a Balanced Lifestyle:',
          description:
            'Practice {{stress}} management techniques, maintain a regular {{fitness}} routine, and ensure adequate sleep to support overall well-being and academic performance.',
        },
        {
          title: 'Healthy Social and Support Networks:',
          description:
            'Surround yourself with supportive peers and mentors who encourage personal and academic growth. Utilize campus resources like counseling services when needed.',
        },
        {
          title: 'Build Resilience:',
          description:
            'Develop strategies for coping with setbacks, whether academic or personal, to maintain a positive mindset and resilience in the face of challenges.',
        },
      ],

      Finances: [
        {
          title: 'Financial Management Skills:',
          description:
            'Develop advanced proficiency in {{budgeting}}, managing student loans, and understanding {{financial aid}} options. Create a financial plan for post-graduation, including potential relocation or job search expenses.',
        },
        {
          title: 'Pursue Scholarships and Grants:',
          description:
            'Actively apply for scholarships, grants, and fellowships to support your educational and professional development.',
        },
        {
          title: 'Savings and Emergency Funds:',
          description:
            'Aim to save a minimum of $2,500 for unexpected expenses related to career development, such as {{interview}} travel or professional attire.',
        },
      ],
      Mindsets: [
        {
          description: 'Willingness to work very hard and make sacrifices for the future.',
        },
        {
          title: 'Growth and Professional Mindset:',
          description:
            'Cultivate a {{growth mindset}} focused on continuous improvement, professional development, and willingness to learn from both successes and failures.',
        },
        {
          title: 'Work Ethic and Initiative:',
          description:
            'Demonstrate a strong {{work ethic}} by taking initiative in academic and professional settings. Seek out opportunities for growth and take on challenging projects.',
        },
        {
          title: 'Commitment to Ethics and Integrity:',
          description:
            'Uphold ethical standards in all academic and professional endeavors. Show respect, {{responsibility}}, and {{empathy}} in your interactions, laying a foundation for trust and credibility in your career.',
        },
      ],
    },
    'Top-Tier Professional': {
      'Post-secondary objectives': [
        {
          title: 'Pursue a Rigorous and Relevant Major:',
          description:
            'Remain enrolled in a competitive, rigorous major that aligns with your intended top-tier professional path, ensuring a high level of academic challenge.',
        },
        {
          title: 'Engage in top-tier Enrichment Programs:',
          description:
            'Participate in honors programs, top-tier internships, research opportunities, or specialized training to distinguish yourself in your field.',
        },
        {
          title: 'Network with University Faculty and Industry Leaders:',
          description:
            'Develop meaningful connections with faculty, alumni, and industry professionals to gain mentorship, recommendations, and insights into top-tier career pathways.',
        },
      ],
      Academics: [
        {
          title: 'Maintain a High GPA:',
          description:
            'Aim for a minimum GPA of 3.8+ on a 4.0 scale, particularly in your major courses.',
        },
        {
          title: 'Pursue Academic Excellence:',
          description:
            'Seek out and earn academic honors, scholarships, or fellowships. Aim for recognition such as Dean’s List, Phi Beta Kappa, or department-specific awards.',
        },
        {
          title: 'Advanced Coursework:',
          description:
            'Enroll in advanced or graduate-level courses, if available, and pursue interdisciplinary studies to broaden your knowledge base and skill set.',
        },
        {
          title: 'Capstone or Thesis:',
          description:
            'Undertake a capstone project, honors thesis, or significant research project to demonstrate deep understanding and commitment to your field.',
        },
        {
          description:
            'Exceptional extracurricular profile including examples of distinctive leadership and achievement',
        },
      ],
      Careers: [
        {
          title: 'Gain High-Quality Internships:',
          description:
            'Secure {{internships}} or part-time positions at top firms, research institutions, or organizations in your field that are known for hiring top-tier professionals.',
        },
        {
          title: 'Professional Skill Building:',
          description:
            'Develop specialized skills relevant to your desired career, such as data analysis, coding, advanced communication, or financial modeling.',
        },
        {
          title: 'Professional Organizations:',
          description:
            'Join and actively participate in professional organizations, student societies, or honor societies related to your field to build your {{resume}} and professional network.',
        },
        {
          title: 'Mentorship and Networking:',
          description:
            'Establish mentorship relationships with at least 3-5 top-tier professionals and leverage university networks to identify potential career opportunities.',
        },
        {
          title: 'Create a Professional Portfolio:',
          description:
            'Develop a comprehensive portfolio of work, projects, research, or writing that showcases your abilities and sets you apart in your field.',
        },
        {
          title: 'Develop a Professional Online Presence:',
          description:
            'Create a LinkedIn profile highlighting your skills, experience, and achievements. Engage in online professional communities related to your field.',
        },
      ],
      Fundamentals: [
        {
          title: 'Advanced Personal Development:',
          description:
            'Achieve advanced proficiency in as many fundamental skills as possible including {{goal setting}}, {{communication}}, leadership, and effective {{time management}}. Cultivate public speaking, negotiation, and professional writing skills.',
        },
        {
          title: 'Seek Constructive Feedback:',
          description:
            'Regularly seek feedback from mentors, professors, and employers to refine your skills and strategies for career success.',
        },
      ],
      Wellness: [
        {
          title: 'Holistic Wellness:',
          description:
            'Prioritize mental, physical, and emotional well-being through regular exercise, balanced nutrition, and {{stress}} management techniques. Seek out campus resources like counseling and wellness centers when needed.',
        },
        {
          title: 'Healthy Social Network:',
          description:
            'Surround yourself with a supportive and motivated peer group. Foster relationships that encourage personal growth and maintain a balanced social life.',
        },
        {
          title: 'Resilience and Stress Management:',
          description:
            'Develop advanced strategies for coping with academic and career-related {{stress}}, maintaining resilience in the face of setbacks.',
        },
      ],
      Finances: [
        {
          title: 'Financial Literacy and Planning:',
          description:
            'Demonstrate advanced proficiency in {{budgeting}}, {{investing}}, managing student loans, and understanding salary potential and benefits in top-tier careers.',
        },
        {
          title: 'Pursue Financial Support:',
          description:
            'Apply for scholarships, grants, and assistantships to alleviate educational costs. Plan for post-graduation financial needs, including potential relocation or further education.',
        },
        {
          title: 'Build a Financial Safety Net:',
          description:
            'Aim to save a minimum of $2,500 to cover unexpected expenses related to career development, such as {{interviewing}}, professional attire, or conferences.',
        },
      ],
      Mindsets: [
        {
          title: 'Top-Tier Professional Mindset:',
          description:
            'Cultivate a mindset focused on excellence, resilience, and lifelong learning. Embrace challenges as opportunities for growth and continuously push beyond your comfort zone. Willingness to work extremely hard and make substantial sacrifices for the future.',
        },
        {
          title: 'Adaptability and Innovation:',
          description:
            'Develop the ability to adapt to changing circumstances, embrace innovation, and think strategically about complex problems.',
        },
        {
          title: 'Commitment to Impact:',
          description:
            'Stay committed to making a meaningful impact in your field, whether through research, community involvement, or professional contributions. Demonstrate generosity, {{empathy}}, and ethical responsibility in your academic and professional endeavors.',
        },
      ],
    },
  },
};
